/**
 * /auth/verify-email-change?token=... — click target of the
 * change-email verification email triggered from Account Settings →
 * Profile (#547 PR 5).
 *
 * Symmetric with VerifyEmail.tsx: post the token to Better Auth's
 * verification endpoint, surface a real UI rather than the backend's
 * JSON. The change is APPLIED on success — `user.email` flips to the
 * new value, sessions stay valid, the next /api/me read reflects the
 * new address.
 *
 * Difference from signup verification: the user is (typically) still
 * signed in when they click. We refresh identity so the Settings page
 * (or any other surface they navigate to) picks up the new email
 * immediately. If they're signed out — clicked the link from a
 * different device or after explicit logout — we fall back to a
 * "verified, please sign in" success card.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'

import { authClient } from '@/auth/authClient'
import { AuthLayout } from '@/auth/AuthLayout'
import { identityAPI } from '@/lib/api'

type Status = 'pending' | 'success' | 'error'

export function VerifyEmailChange() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''
  const [status, setStatus] = useState<Status>(token ? 'pending' : 'error')
  const [errorMessage, setErrorMessage] = useState<string>(
    token
      ? ''
      : 'Verification link is missing its token. The link may have been truncated by your email client.',
  )
  const [stillSignedIn, setStillSignedIn] = useState<boolean | null>(null)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    void (async () => {
      // Better Auth surfaces email-change verification on the same
      // verifyEmail endpoint (the token kind is encoded in the JWT
      // payload). Calling verifyEmail here is correct.
      const { error } = await authClient.verifyEmail({ query: { token } })
      if (cancelled) return
      if (error) {
        setStatus('error')
        setErrorMessage(
          error.message ?? 'Verification failed. The link may have expired.',
        )
      } else {
        setStatus('success')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  useEffect(() => {
    if (status !== 'success') return
    let cancelled = false
    void (async () => {
      try {
        const me = await identityAPI.me()
        if (cancelled) return
        // If the user is still signed in (most common case — they
        // clicked the link on the same device they requested the
        // change from), forward back to the Profile tab so they see
        // the updated email immediately. Otherwise show the
        // standalone "verified, please sign in" card.
        if (me) {
          setStillSignedIn(true)
          // Settings is workspace-scoped — `/settings` alone has no
          // route; navigating there would fall through to
          // WorkspaceRedirect which strips the query string. Route
          // through `/account` so AccountRedirect resolves the
          // user's default workspace and re-adds
          // `?tab=account.profile` for us. Brief delay so the
          // success check is visible before the redirect chain
          // resolves — avoids the jarring instant cut.
          // PR #563 Copilot review.
          setTimeout(() => {
            if (!cancelled) {
              navigate('/account', { replace: true })
            }
          }, 800)
        } else {
          setStillSignedIn(false)
        }
      } catch {
        if (cancelled) return
        setStillSignedIn(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [status, navigate])

  if (status === 'pending') {
    return (
      <AuthLayout title="Confirming your new email…">
        <div className="flex flex-col items-center gap-3 py-2" role="status" aria-live="polite">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">This usually takes a moment.</p>
        </div>
      </AuthLayout>
    )
  }

  if (status === 'success') {
    return (
      <AuthLayout
        title="Email updated"
        subtitle={
          stillSignedIn === true
            ? 'Taking you back to your profile…'
            : stillSignedIn === false
            ? 'Sign in with your new email to continue.'
            : 'Your account email is now updated.'
        }
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <CheckCircle2 className="h-10 w-10 text-success" aria-hidden="true" />
          {stillSignedIn === true && (
            <Loader2
              className="h-4 w-4 animate-spin text-muted-foreground"
              aria-hidden="true"
            />
          )}
          {stillSignedIn !== true && (
            <Link to="/auth/login" className="text-sm text-primary hover:underline">
              Go to sign in
            </Link>
          )}
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Email change failed" subtitle={errorMessage}>
      <div className="flex flex-col items-center gap-4 py-2">
        <XCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
        <p className="text-sm text-muted-foreground text-center">
          The link may have expired or already been used. Try the change
          again from Account Settings → Profile.
        </p>
        {/* `/account` lets AccountRedirect resolve the user's
            default workspace and land them on the Profile tab —
            `/settings` itself isn't a route (Settings is
            workspace-scoped). PR #563 Copilot review. */}
        <Link
          to="/account"
          className="text-sm text-primary hover:underline"
        >
          Back to profile
        </Link>
      </div>
    </AuthLayout>
  )
}
